import { useEffect, useRef, type CSSProperties } from "react";
import { Check, X } from "lucide-react";
import { Button, Screen, Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import s from "./SubmittedScreen.module.css";

export interface SubmittedScreenProps {
  onClose(): void;
  /** Right after a US submit: the report-ready moment instead of the plain end state. */
  celebrate?: boolean;
  /** Opens the newly created report. Absent if its id couldn't be fetched. */
  onViewReport?: () => void;
}

const BURST_DOTS = 12;

/** Screen 5 of the deck, reached either right after a successful `submit()`
    or directly on load whenever `state === "submitted"` (reopening an
    already-submitted questionnaire, or a 409 `already_submitted` mid-write
    redirecting here -- both handled upstream in `useQuestionnaire`). No
    control here writes anything -- this is the read-only end state, and
    `QuestionnaireOverlayContent`'s own early return for `state ===
    "submitted"` is what keeps every editable screen (the hub and its sections)
    unreachable once submitted, not anything in this component. */
export function SubmittedScreen({ onClose, celebrate = false, onViewReport }: SubmittedScreenProps) {
  const { t } = useTranslation("client");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.querySelector<HTMLElement>("h1")?.focus({ preventScroll: true });
  }, []);

  return (
    <Screen className={s.root}>
      <div className={s.header}>
        <button type="button" className={s.close} onClick={onClose} aria-label={t("questionnaire.close", "Sulje")}>
          <X aria-hidden size={20} />
        </button>
      </div>

      <div className={s.centered} ref={containerRef}>
        {celebrate && (
          <div className={s.mark} aria-hidden>
            <div className={s.burst}>
              {Array.from({ length: BURST_DOTS }, (_, index) => (
                <span key={index} className={s.dot} style={{ "--dot-angle": `${(360 / BURST_DOTS) * index}deg` } as CSSProperties} />
              ))}
            </div>
            <span className={s.check}>
              <Check size={40} strokeWidth={3} />
            </span>
          </div>
        )}
        <Text as="h1" size="2xl" weight="bold" tabIndex={-1}>
          {celebrate
            ? t("questionnaire.submitted.reportReady", "Henkilökohtainen rautaraporttisi on valmis!")
            : t("questionnaire.submitted.title", "Esitiedot lähetetty")}
        </Text>
        <Text as="p" size="md" className={s.body}>
          {t("questionnaire.submitted.body", "Kiitos! Lääkäri käy tietosi läpi ennen tapaamistasi.")}
        </Text>
      </div>

      <div className={s.footer}>
        {celebrate && onViewReport ? (
          <Button className={s.primary} onClick={onViewReport}>
            {t("questionnaire.submitted.viewReport", "Näytä raporttini")}
          </Button>
        ) : (
          <Button className={s.primary} onClick={onClose}>
            {t("questionnaire.submitted.next", "Seuraavaksi")}
          </Button>
        )}
      </div>
    </Screen>
  );
}
